import { AxiosError } from 'axios';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import styled from 'styled-components';
import {
  postRefundValidation,
  refundRegister,
  temporaryStorageSave,
} from '@/api/refund';
import {
  IRefundPayload,
  IRefundResponse,
  StoragePayload,
} from '@/types/refund';
import { getRefundAmount } from '@/util/getRefundAmount';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { refundIndexState } from '@/store/modules/user';
import { reset, setPrivacyAgreementDate } from '@/store/modules/refund';
import LeafletModal from '@/components/refund/LeafletModal';
import getCurrentTime, { getNumberFullTime } from '@/util/getCurrentTime';
import { attachedHyphensDate, convertToKoreanUnit } from '@/util/format';
import { updateModal } from '@/store/modules/modal';
import confirmIcon from '@/assets/common/confirm.png';
import { updateLoading } from '@/store/loading.store';

import useScanLog from '@/hooks/useScanLog';
import { debounce } from 'lodash';
import useGetGlobalStatus from '@/hooks/useGetGlobalStatus';

import useRefundCheckDataSubscribe from '@/hooks/useRefundCheckDataSubscribe';
import useDeleteRefundCheck from '@/hooks/useDeleteRefundCheck';
import { setTotalPaymentAmount, setTotalPrice } from '@/store/modules/tablet';
import { setUuid } from '@/store/modules/scan';
import Inko from 'inko';
import DuplicateRefundModal from '@/components/refund/DuplicateRefundModal';

import AmountDetail from '../discount/AmountDetail';
import ChartNumberModal from '../refund/ChartNumberModal';
import TouristInput from './TouristInput';
import { ISubmitData } from '@/types/discount.type';

const orderInfoList = {
  productType: '',
  price: '',
  supplyPrice: '',
  vatPrice: '',
  isCheck: false,
};

interface IProps {
  setIsRefund: Dispatch<SetStateAction<boolean>>;
  isExpired: boolean;
}

function DiscountContent({ setIsRefund, isExpired }: IProps) {
  const { globalStatus } = useGetGlobalStatus();
  const [isPayment, setIsPayment] = useState(true);
  const [leafletModalInfo, setLeafletModalInfo] = useState({
    active: false,
    isOver600: false,
    isKioskError: false,
  });
  const [isOpenChartModal, setIsOpenChartModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState({
    isSubmit: false,
    message: '',
  });
  const [isFetchedStorageData, setIsFetchedStorageData] = useState(false);
  const [isDuplicateRefund, setIsDuplicateRefund] = useState(false);

  const { touristIndex, hospitalId } = useAppSelector((state) => state.user);
  const inko = new Inko();
  const { isScanned, temporaryStorage, privacyAgreementDate } = useAppSelector(
    (state) => state.refund,
  );
  const { isPassportCheck } = useAppSelector((state) => state.validate);
  const dispatch = useAppDispatch();

  // 의료 태블릿
  const privacyAgreementDateRef = useRef('');
  const { currentTabletID } = useAppSelector((state) => state.tabletId);
  const { refundInfo } = useRefundCheckDataSubscribe(
    hospitalId.toString(),
    currentTabletID,
  );
  const refundInfoIdRef = useRef(refundInfo?.id);
  const { mutateAsync: deleteRefundCheckMutate } = useDeleteRefundCheck(
    hospitalId.toString(),
    currentTabletID,
  );

  // 환급하기 할때 서버로 보내는 body 값
  const methods = useForm({
    defaultValues: {
      touristName: '',
      departureDate: '',
      paymentCard: '0',
      paymentCash: '0',
      supply: [orderInfoList, orderInfoList, orderInfoList],
      supplyDate: attachedHyphensDate(getNumberFullTime(new Date())),
      name: '',
      nationality: '',
      passportNumber: '',
      saleDate: getCurrentTime(),
      birthYear: '',
      birthMonth: { label: '', value: '' },
      birthDay: '',
      gender: 'F',
    },
  });
  const { handleSubmit, setValue, setFocus, getValues } = methods;

  // TODO - 임시저장 환급 에러 참고
  // 임시저장 값 불러오기
  useEffect(() => {
    if (temporaryStorage.supplyDate) {
      setValue('supplyDate', temporaryStorage.supplyDate);
      setValue('touristName', temporaryStorage.touristName);
      setValue('passportNumber', temporaryStorage.passportNumber);
      setValue('nationality', temporaryStorage.nationality);
      setValue('paymentCash', temporaryStorage.paymentCash);
      setValue('paymentCard', temporaryStorage.paymentCard);
      setIsFetchedStorageData(true);
    }
  }, [temporaryStorage]);

  const onCloseModal = (type?: string) => {
    dispatch(updateModal({ isActive: false, data: null, type: type || null }));
  };

  const deleteRefundCheck = async () => {
    if (refundInfoIdRef.current) {
      await deleteRefundCheckMutate({
        refundId: refundInfoIdRef.current,
      });
    }
  };

  const onCloseModalWithDeleteRefund = async (type?: string) => {
    dispatch(updateModal({ isActive: false, data: null, type: type || null }));
    deleteRefundCheck();
  };

  const queryClient = useQueryClient();
  const { mutate: storageMutation } = useMutation<
    number,
    AxiosError,
    StoragePayload
  >((payload) => temporaryStorageSave(payload), {
    retry: false,
    onSuccess: () => {
      dispatch(
        updateModal({
          isActive: true,
          type: 'CUSTOM',
          data: {
            icon: confirmIcon,
            title: '임시 저장 성공',
            content: '입력하신 정보를 임시저장했습니다.',
            btnText: '확인',
            subBtnText: '',
            onClose: onCloseModal,
          },
        }),
      );
      queryClient.invalidateQueries({ queryKey: 'allStorage' });
    },
    onError: (error) => {
      deleteRefundCheck();
      console.log('에러', error);
    },
  });

  const { mutate: refundMutation, isLoading } = useMutation<
    IRefundResponse,
    AxiosError,
    IRefundPayload
  >((payload) => refundRegister(payload), {
    retry: false,
    onSuccess: (data) => {
      dispatch(refundIndexState(data));
      dispatch(reset());
      setIsRefund(true);
      setIsPayment(false);
      dispatch(updateLoading({ isLoading: false }));
    },
    onError: (error) => {
      deleteRefundCheck();
      dispatch(updateLoading({ isLoading: false }));
      //@ts-ignore
      const { code, message } = error.response?.data;
      switch (code) {
        case 'H0006':
          alert(
            '유치기관 만료일을 초과했습니다. 만료일 갱신 후 다시 시도해 주세요.',
          );
          break;
        default:
          if (message) {
            return alert(message);
          }
          return alert('오류가 발생했습니다. 잠시후 다시 시도해주세요.');
      }
    },
  });

  const onSubmit: SubmitHandler<ISubmitData> = (data) => {
    const {
      paymentCard,
      paymentCash,
      totalPayPrice,
      supplyDate,
      touristName,
      gender,
    } = data;

    const totalPrice = Number(totalPayPrice);
    let totalRefund = 0;
    const newPaymentCard = paymentCard.replace(/(,)/g, '');
    const newPaymentCash = paymentCash.replace(/(,)/g, '');
    console.log(data);
    // if (newSupply.length === 0) {
    //   alert('시술 내역을 입력해주세요.');
    //   return;
    // }
    console.log('totalPrice', totalPrice, paymentCard, paymentCash);

    totalRefund = getRefundAmount(totalPrice);

    if (totalPrice < 15000) {
      setErrorMessage((prev) => ({
        ...prev,
        isSubmit: true,
        message: '최소 금액 15,000원 이상 입니다.',
      }));
      return;
    }

    const totalPaymentAmount = totalPrice - getRefundAmount(totalPrice);
    dispatch(setTotalPaymentAmount(totalPaymentAmount));
    dispatch(setTotalPrice(totalPrice));

    const payload: IRefundPayload = {
      touristName: touristName.toUpperCase(),
      supplyDate,
      totalRefund: String(totalRefund),
      totalPrice: String(totalPrice),
      paymentCard: String(newPaymentCard),
      paymentCash: String(newPaymentCash),
      touristId: String(touristIndex),
      orderInfoList: [],
      paymentType: 'ORIGINAL',
      isScanned,
      tempInformationId: temporaryStorage.tempInformationId,
      gender: gender,
    };

    // TODO - 임시저장 환급 에러 참고
    // 차감결제인 경우, 총액 비교
    const payloadTotal =
      Number(payload.paymentCard) +
      Number(payload.paymentCash) +
      Number(payload.totalRefund);
    console.log(
      payload.paymentCard,
      payload.paymentCash,
      payload.totalRefund,
      Number(payload.totalPrice),
      payloadTotal,
    );

    if (Number(payload.totalPrice) !== payloadTotal) {
      deleteRefundCheck();
      return alert(
        '환급 생성 중 오류가 발생했습니다. 새로고침 후 다시 시도해 주세요.',
      );
    }

    dispatch(
      updateModal({
        isActive: true,
        type: 'PRICE_CHECK',
        data: {
          icon: '',
          title: '결제금액을 확인해주세요. ',
          content: convertToKoreanUnit(totalPaymentAmount) + '원',
          btnText: '확인',
          subBtnText: '다시 입력',
          onClose: onCloseModalWithDeleteRefund,
          btnCallback: () => {
            dispatch(updateLoading({ isLoading: true }));
            onCloseModal();
            debounceRefundValidate(
              () => {
                if (privacyAgreementDateRef.current) {
                  payload.privacyAgreementDate =
                    privacyAgreementDateRef.current;
                }
                refundMutation(payload);
                dispatch(setPrivacyAgreementDate(''));
              },
              supplyDate,
              totalPrice,
            );
          },
        },
      }),
    );
  };

  const debounceRefundValidate = debounce(
    async (refundFn, supplyDate, totalPrice) => {
      try {
        const { items } = await postRefundValidation({
          touristId: touristIndex,
          supplyDate,
          totalPrice: totalPrice.toString(),
        });
        dispatch(updateLoading({ isLoading: false }));
        if (items.length !== 0) {
          dispatch(
            updateModal({
              isActive: true,
              type: 'SAME_REFUND_CHECK',
              data: {
                supplyDate,
                data: items,
                onClick: () => {
                  dispatch(updateLoading({ isLoading: true }));
                  refundFn();
                },
                onClose: onCloseModalWithDeleteRefund,
              },
            }),
          );
        } else {
          dispatch(updateLoading({ isLoading: true }));
          refundFn();
          onCloseModal();
        }
      } catch (error) {
        console.error('환급 검증 에러', error);
        dispatch(updateLoading({ isLoading: false }));
        alert('알 수 없는 문제가 발생했습니다. 잠시 후 다시 시도해주세요.');
      }
    },
    200,
  );

  const onClickStorage = () => {
    const {
      touristName,
      nationality,
      passportNumber,
      supplyDate,
      supply,
      birthYear,
      birthMonth,
      birthDay,
      paymentCard,
      paymentCash,
    } = methods.getValues();

    const payload = {
      hospitalId,
      supplyDate: supplyDate,
      nation: nationality,
      name: touristName.toUpperCase(),
      passportNumber,
      paymentCash,
      paymentCard,
      orderInfoList: supply,
      year: birthYear,
      month: birthMonth.value,
      day: birthDay,
      tempInformationId: temporaryStorage.tempInformationId,
    };
    storageMutation(payload);
  };

  const focusTouristNameWhenClickAnywhere = (e: any) => {
    const { id, tagName } = e.target;
    if (
      id !== 'no_focus' &&
      !['INPUT', 'BUTTON', 'LI', 'IMG', 'UL'].includes(tagName)
    ) {
      setFocus('touristName');
    }
  };

  // 커스텀 의원
  const [isOpen, setIsOpen] = useState(false);
  // 입력값 로그 저장
  const { mutate: postLogNCrash } = useScanLog();

  const inputValue = useRef('');
  const readFirstLine = useRef(false);

  useEffect(() => {
    const postLog = (body: string, traceId: string) => {
      postLogNCrash(
        { body, traceId },
        {
          onSuccess: () => {
            inputValue.current = '';
          },
        },
      );
    };

    const UUID = new Date().getTime().toString();
    dispatch(setUuid(UUID));

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Shift' && e.key !== 'Enter') {
        inputValue.current += e.key;
      }

      if (e.key === 'Enter' && inputValue.current) {
        const mrz = inko.ko2en(inputValue.current).toUpperCase();

        if (!readFirstLine.current) {
          readFirstLine.current = true;
        } else {
          postLog(
            `여권조회 성공 후 스캔 재시도 = ${isPassportCheck}, 병원명 = ${localStorage.getItem(
              'name',
            )}, 입력값 = ${mrz}`,
            UUID,
          );
          inputValue.current = '';
          readFirstLine.current = false;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPassportCheck]);

  const modalRef = useRef<HTMLButtonElement | null>(null);
  const modalEl = modalRef.current;

  useEffect(() => {
    if (modalEl) {
      setTimeout(() => modalEl.focus(), 200);
    }
  }, [modalEl]);

  useEffect(() => {
    if (privacyAgreementDate) {
      privacyAgreementDateRef.current = privacyAgreementDate;
    }
  }, [privacyAgreementDate]);

  useEffect(() => {
    if (refundInfo?.id) {
      refundInfoIdRef.current = refundInfo.id;
    }
  }, [refundInfo]);

  return (
    <FormProvider {...methods}>
      <Container onClick={focusTouristNameWhenClickAnywhere}>
        <TouristInput
          setLeafletModalInfo={setLeafletModalInfo}
          setIsRefund={setIsRefund}
          setIsPayment={setIsPayment}
          isExpired={isExpired}
          setIsOpen={setIsOpen}
          setIsOpenChartModal={setIsOpenChartModal}
          setIsDuplicateRefund={setIsDuplicateRefund}
        />
        <AmountDetail
          isCustomKor={false}
          isPayment={isPayment}
          setLeafletModalInfo={setLeafletModalInfo}
          errorMessage={errorMessage}
          onClickSaveBtn={onClickStorage}
          onClickRefundBtn={handleSubmit(onSubmit)}
          isPassportCheck={isPassportCheck}
          isFetchedStorageData={isFetchedStorageData}
          isLoading={isLoading}
          supplyList={getValues('supply')}
          setIsDuplicateRefund={setIsDuplicateRefund}
        />
      </Container>

      {/* 고객 차트번호 입력 필요한 경우 */}
      {globalStatus?.isChartNumberEnabled && isOpenChartModal && (
        <ChartNumberModal onClose={() => setIsOpenChartModal(false)} />
      )}

      {isDuplicateRefund && (
        <DuplicateRefundModal onClose={() => setIsDuplicateRefund(false)} />
      )}
    </FormProvider>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  gap: 36px;
  flex: 1;
  padding: 22px 40px 85px 40px;
  @media ${(props) => props.theme.laptop} {
    gap: 24px;
  }
`;

export default DiscountContent;
